import { Link, router } from "@inertiajs/react"
import { CheckCheck, MailQuestion } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { Button } from "@/components/ui/button"
import { candidatesPath } from "@/routes"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-953 — Brand/Home § « Invitations en veille » (segment 5).
 *
 * `invited` candidates whose LIVE invite has shown no sign of life for 7 days
 * (PRD R3). A creator we already won, evaporating quietly.
 *
 * **Why a separate query and not a status list.** Membership depends on
 * whether a non-revoked, non-completed invite exists and how long since it
 * last showed life — `statuses: ["invited"]` would surface every invited
 * candidate whether or not their link had gone quiet.
 *
 * 🔴 **This section's clock is the INVITE's, not the status's.** PRD R2 ranks
 * segment 5 on last invite activity, so a card can read « silencieux·se depuis
 * 12 jours » for a candidate who entered `invited` 90 days ago. That is
 * correct. The shared card's wait cue renders off the same two fields as its
 * four siblings, which is what keeps one card component instead of five.
 *
 * **Two sub-states, two different fixes** (PRD R3): never-opened means the
 * message probably never landed, so the answer is to re-share the link;
 * opened-but-unfinished means they hesitated, so the answer is to open the
 * candidate and talk to them. Every sub-state has a real action, which is what
 * makes this a queue rather than a list.
 */
export interface InvitesGoingQuietSectionProps {
  section: SectionData
}

export function InvitesGoingQuietSection({
  section,
}: InvitesGoingQuietSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["candidates_invites_quiet"],
      onFinish: () => setIsRetrying(false),
    })
  }

  return (
    <HomeTriageSection
      title={t("candidates.home_invite_stale.title")}
      subtitle={t("candidates.home_invite_stale.subtitle")}
      state={isRetrying ? "loading" : section.state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="home-invite-stale"
      // OKS-953 (PRD R7) — one of the FIVE candidate segments that collapse.
      collapseWhenEmpty
      emptyTestId="home-invite-stale-empty"
      renderColdStart={() => (
        <EmptyState
          icon={MailQuestion}
          title={t("candidates.home_invite_stale.cold_start.title")}
          titleAs="h2"
          description={t("candidates.home_invite_stale.cold_start.description")}
          data-testid="home-invite-stale-cold-start"
          actions={
            <Button asChild variant="outline">
              {/* The upstream segment: contacted candidates awaiting an invite. */}
              <Link href={candidatesPath({ status: "contacted" })}>
                {t("candidates.home_invite_stale.cold_start.cta")}
              </Link>
            </Button>
          }
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCheck}
          title={t("candidates.home_invite_stale.empty.title")}
          titleAs="h2"
          description={t("candidates.home_invite_stale.empty.description")}
          data-testid="home-invite-stale-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
